import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import OptionRowsEditor from "../../components/admin/OptionRowsEditor";
import Pagination from "../../components/admin/Pagination";
import { fetchCategories, fetchDeviceById } from "../../services/deviceApi";
import {
  fetchDeviceQuestionOverrides,
  formatOptionImpacts,
  optionRowsToPayload,
  resetDeviceQuestionOverride,
  updateDeviceQuestionOverride,
} from "../../services/questionApi";
import { optionRowsFromQuestion } from "../../utils/assessmentFormat";
import "../../styles/admin-common.css";

const PAGE_SIZE = 15;

const ModelQuestionOverrides = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [impactRows, setImpactRows] = useState([]);
  const [priceDraft, setPriceDraft] = useState("");

  const productTypeName = categories.find((c) => String(c.id) === String(device?.categoryId))?.name || "—";

  const load = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError("");
    try {
      const [dev, cats, list] = await Promise.all([
        fetchDeviceById(deviceId),
        fetchCategories(),
        fetchDeviceQuestionOverrides(deviceId),
      ]);
      setDevice(dev);
      setCategories(cats);
      setQuestions(list);
    } catch (err) {
      setError(err.message);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
  const pageQuestions = questions.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  const startEdit = (q) => {
    setEditingId(q.templateQuestionId);
    const rows = optionRowsFromQuestion({
      options: q.options,
      optionImpacts: q.optionImpacts || q.defaultOptionImpacts,
      optionLabels: q.optionLabels,
    });
    setImpactRows(rows);
    setPriceDraft(String(q.priceImpact ?? q.defaultPriceImpact ?? ""));
    setSuccess("");
  };

  const saveOverride = async (q) => {
    setError("");
    setSuccess("");
    try {
      const { optionImpacts } = optionRowsToPayload(impactRows);
      await updateDeviceQuestionOverride(deviceId, q.templateQuestionId, {
        optionImpacts,
        priceImpact: priceDraft === "" ? 0 : parseFloat(priceDraft),
      });
      setEditingId(null);
      setSuccess("Impacts updated.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = async (q) => {
    setError("");
    setSuccess("");
    try {
      await resetDeviceQuestionOverride(deviceId, q.templateQuestionId);
      setEditingId(null);
      setSuccess("Reset to type default.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!deviceId) {
    return (
      <div className="admin-page">
        <p className="error-msg">Missing model id.</p>
        <Link to="/models">← Back to models</Link>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <p className="breadcrumb">
        <Link to="/models">Models</Link>
        <span> / </span>
        <span>{device ? `${device.brandName} ${device.modelName}` : "…"}</span>
        <span> / Questions</span>
      </p>

      <h2>Model question overrides</h2>
      <p className="subtitle">
        {device ? (
          <>
            <strong>{device.brandName} {device.modelName}</strong> inherits questions from the{" "}
            <strong>{productTypeName}</strong> central catalog. Adjust option impacts for this model only.
            Option labels come from the central catalog.
          </>
        ) : (
          "Loading model…"
        )}
      </p>

      {error && <p className="error-msg">{error}</p>}
      {success && <p className="success-msg">{success}</p>}

      <div className="admin-filters">
        <button type="button" className="admin-btn secondary" onClick={() => navigate("/models")}>
          ← Back to models
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : questions.length === 0 ? (
        <p className="empty-msg">
          No central questions for {productTypeName}.{" "}
          <Link to={`/questions?categoryId=${device?.categoryId || ""}`}>Add questions to the product type catalog</Link>.
        </p>
      ) : (
        <>
          <p className="subtitle">{questions.length} inherited question{questions.length === 1 ? "" : "s"}</p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Group</th>
                <th>Question</th>
                <th>Type default impacts</th>
                <th>Model impacts</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageQuestions.map((q) => {
                const isEditing = editingId === q.templateQuestionId;
                const hasOverride = q.overrideQuestionId != null;
                return (
                  <tr key={q.templateQuestionId}>
                    <td><code>{q.category}</code></td>
                    <td>{q.question}</td>
                    <td><small>{formatOptionImpacts(q.defaultOptionImpacts)} · {q.defaultPriceImpact ?? 0}%</small></td>
                    <td>
                      {isEditing ? (
                        <div className="stack-fields">
                          <OptionRowsEditor
                            rows={impactRows}
                            onChange={setImpactRows}
                            showImpact
                          />
                          <label className="field-label" style={{ marginTop: 8 }}>
                            Default impact %
                            <input
                              type="number"
                              value={priceDraft}
                              onChange={(e) => setPriceDraft(e.target.value)}
                              style={{ width: 120 }}
                            />
                          </label>
                        </div>
                      ) : (
                        <small>
                          {formatOptionImpacts(q.optionImpacts)} · {q.priceImpact ?? 0}%
                          {hasOverride && <span className="field-hint"> (override)</span>}
                        </small>
                      )}
                    </td>
                    <td className="actions-cell">
                      {isEditing ? (
                        <>
                          <button type="button" className="admin-btn" onClick={() => saveOverride(q)}>Save</button>
                          <button type="button" className="admin-btn secondary" onClick={() => setEditingId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="admin-btn secondary" onClick={() => startEdit(q)}>Edit impacts</button>
                          {hasOverride && (
                            <button type="button" className="admin-btn secondary" onClick={() => handleReset(q)}>Reset</button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </>
      )}
    </div>
  );
};

export default ModelQuestionOverrides;
